import { useMemo } from 'react';

import { VisibilityState } from '@tanstack/react-table';

import { IconApplicants } from '@components/icons/applicants';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconDate } from '@components/icons/date';
import { IconEmail } from '@components/icons/email';
import { IconField } from '@components/icons/field';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconIssues } from '@components/icons/issues';
import { IconPhone } from '@components/icons/phone';
import { IconStatistics } from '@components/icons/statistics';
import { IconStatus } from '@components/icons/status';
import { IconTraveller } from '@components/icons/traveller';
import { IconTravellerStatusCompleted } from '@components/icons/traveller-status-completed';
import { IconTravellerStatusInProgress } from '@components/icons/traveller-status-in-progress';
import { IconTravellerStatusNotStarted } from '@components/icons/traveller-status-not-started';
import { IconTravellerStatusPaused } from '@components/icons/traveller-status-paused';
import { CategoryFilterFields, FilterConfig, FilterConfigField } from '@components/table';
import { ColumnIcon } from '@components/table/column-icon';
import { TableStore } from '@components/table/table-store';

import { AttributeType } from 'api/attributes/requests';
import { TravellerAttributeTemplate } from 'api/attributes/requests';
import { JourneyUserSettingsState } from 'api/journeys/requests';
import { useLists } from 'api/lists/hooks';
import { List } from 'api/lists/requests';
import { ActionsDataAction, ActionsDataField } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { User } from 'api/users/requests';

import { makeColumnId, makeVariableColumnId } from './columns';
import { actionStatusValues, excludedActionTypes } from './constants';
import { ExtraStateStore } from './extra-state-store';
import { t } from './locale';

import { useAttributesFilterConfig } from '../../company-travellers/use-attributes-filter-config';

export const useFilterConfig = (
  allowedAttributes: Array<AttributeType | TravellerAttributeTemplate>,
  extraStateStore: ExtraStateStore,
  tableStore: TableStore,
  actionsData: Array<ActionsDataAction>
) => {
  const { isLoading: attributesLoading, configs: attributesConfig } =
    useAttributesFilterConfig(allowedAttributes);
  const { useCustomNames } = extraStateStore;
  const customNames = useCustomNames();
  const listsQuery = useLists('?include_hidden=1');
  const usersQuery = useUsers();
  const { useColumnVisibility } = tableStore;
  const { columnVisibility } = useColumnVisibility();

  const isLoading = attributesLoading || listsQuery.isLoading || usersQuery.isLoading;

  const baseConfig = useMemo(() => {
    const filterConfig = [
      createJourneyStatusFilter(),
      createAttributesFilter(attributesConfig.traveller),
      createDatesFilter(),
      createProgressFilter(),
      createIssuesFilter(),
    ] satisfies FilterConfig;

    if (attributesConfig.journey.length > 0) {
      filterConfig.push(createJourneyFieldsConfig(attributesConfig.journey));
    }

    return filterConfig;
  }, [attributesConfig]);

  const actionConfigs = useMemo(() => {
    const actions = actionsData.filter((action) => {
      if (excludedActionTypes.includes(action.action)) {
        return false;
      }
      return true;
    });

    let actionConfigs: CategoryFilterFields = [];
    actions.forEach((action) => {
      const statusFilter = createStatusFilter(action, columnVisibility, customNames);
      if (statusFilter) {
        actionConfigs.push(statusFilter);
      }
      const dateFilter = createDateCompletedFilter(action, columnVisibility, customNames);
      if (dateFilter) {
        actionConfigs.push(dateFilter);
      }
      actionConfigs = actionConfigs.concat(
        createVariableFields(
          action,
          columnVisibility,
          customNames,
          actionsData,
          listsQuery.data,
          usersQuery.data
        )
      );
    });

    return actionConfigs;
  }, [actionsData, columnVisibility, customNames, listsQuery.data, usersQuery.data]);

  const config = useMemo(() => {
    if (actionConfigs.length === 0) {
      return baseConfig;
    }

    const actionFieldsGroup = [createActionFieldsFilter(actionConfigs)];

    return baseConfig.concat(actionFieldsGroup);
  }, [actionConfigs, baseConfig]);

  return { isLoading, config };
};

const createIssuesFilter = (): FilterConfigField => ({
  id: 'issues',
  icon: <IconIssues />,
  label: t('issues'),
  type: 'issues',
  values: [
    {
      icon: <IconFormIndicatorError />,
      label: t('error'),
      value: 'error',
    },
    {
      icon: <IconFormIndicatorWarning />,
      label: t('warning'),
      value: 'warning',
    },
    {
      label: t('noIssues'),
      value: 'no-issue',
    },
  ],
});

const createActionFieldsFilter = (fields: CategoryFilterFields): FilterConfigField => ({
  id: 'actionFields',
  type: 'category',
  icon: <IconField />,
  label: t('actionFields'),
  fields,
});

const createJourneyStatusFilter = (): FilterConfigField => ({
  id: 'status',
  icon: <IconStatus />,
  label: 'Traveller journey status',
  type: 'single_select',
  values: [
    {
      value: 'not_started',
      label: t('not_started'),
      icon: <IconTravellerStatusNotStarted />,
    },
    {
      value: 'in_progress',
      label: t('in_progress'),
      icon: <IconTravellerStatusInProgress />,
    },
    { value: 'completed', label: t('completed'), icon: <IconTravellerStatusCompleted /> },
    { value: 'paused', label: t('paused'), icon: <IconTravellerStatusPaused /> },
  ],
  quick: [
    {
      id: 'quick_not_started',
      icon: <IconTravellerStatusNotStarted />,
      label: t('not_started'),
      apply: { comparer: 'is', value: 'not_started' },
    },
    {
      id: 'quick_in_progress',
      label: t('in_progress'),
      icon: <IconTravellerStatusInProgress />,
      apply: { comparer: 'is', value: 'in_progress' },
    },
    {
      id: 'quick_completed',
      label: t('completed'),
      icon: <IconTravellerStatusCompleted />,
      apply: { comparer: 'is', value: 'completed' },
    },
    {
      id: 'quick_paused',
      label: t('paused'),
      icon: <IconTravellerStatusPaused />,
      apply: { comparer: 'is', value: 'paused' },
    },
  ],
});

const createAttributesFilter = (attributesConfig: CategoryFilterFields): FilterConfigField => {
  return {
    id: 'traveller_details',
    icon: <IconTraveller />,
    label: t('profile'),
    type: 'category',
    fields: [
      { id: 'name', type: 'text', icon: <IconApplicants />, label: t('core.fullName') },
      { id: 'email', type: 'email', icon: <IconEmail />, label: t('email') },
      { id: 'phone', type: 'phone_number', icon: <IconPhone />, label: t('core.phone') },
      attributesConfig.length > 0 && {
        id: 'separator',
        type: 'separator',
      },
      ...attributesConfig,
    ].filter(Boolean) as CategoryFilterFields,
  };
};

const createDatesFilter = (): FilterConfigField => ({
  id: 'dates',
  icon: <IconDate />,
  label: t('dates'),
  type: 'category',
  fields: [
    { id: 'dateStarted', type: 'date', label: t('date_started') },
    { id: 'dateCreated', type: 'date', label: t('date_created') },
    { id: 'dateUpdated', type: 'date', label: t('date_modified') },
    { id: 'dateCompleted', type: 'date', label: t('date_completed') },
  ],
});

const createJourneyFieldsConfig = (attributesConfig: CategoryFilterFields): FilterConfigField => ({
  id: 'fields',
  type: 'category',
  icon: <IconAttributeJourney />,
  label: t('journeyFields'),
  fields: attributesConfig,
});

const createProgressFilter = (): FilterConfigField => ({
  id: 'progress',
  icon: <IconStatistics />,
  label: 'Progress',
  type: 'progress',
});

const createStatusFilter = (
  action: ActionsDataAction,
  columnVisibility: VisibilityState,
  customNames?: JourneyUserSettingsState['customNames']
): CategoryFilterFields[number] | undefined => {
  const id = makeColumnId(action.shortId, 'status');
  if (!columnVisibility[id]) {
    return;
  }
  const customStatusName = customNames?.[id];
  const label = customStatusName
    ? `${customStatusName} (${t('action_status')})`
    : t('action_status');

  return {
    id,
    label,
    icon: <IconStatus />,
    type: 'single_select',
    values: actionStatusValues,
  };
};

const createDateCompletedFilter = (
  action: ActionsDataAction,
  columnVisibility: VisibilityState,
  customNames?: JourneyUserSettingsState['customNames']
): CategoryFilterFields[number] | undefined => {
  const id = makeColumnId(action.shortId, 'date_completed');
  if (!columnVisibility[id]) {
    return;
  }
  const customName = customNames?.[id];
  const label = customName
    ? `${customName} (${t('action_date_completed')})`
    : t('action_date_completed');

  return {
    id: id,
    label,
    icon: <IconDate />,
    type: 'date',
  };
};

const getSelectFieldValues = (field: ActionsDataField, lists?: List[]) => {
  if (typeof field.options === 'string') {
    const list = (lists ?? []).find((list) => list.shortId === field.options);
    return list?.options || [];
  }

  if (Array.isArray(field.options)) {
    return field.options.map((opt) => ({
      label: String(opt.label),
      value: opt.value,
    }));
  }

  return null;
};

const createVariableFields = (
  action: ActionsDataAction,
  columnVisibility: VisibilityState,
  customNames: Record<string, string> | undefined,
  actionsData: Array<ActionsDataAction>,
  lists?: List[],
  users?: User[]
): CategoryFilterFields => {
  const fields: CategoryFilterFields = [];
  const data = actionsData.find((a) => a.shortId === action.shortId);
  if (!data) return fields;

  Object.keys(data.variables).forEach((variable) => {
    data.variables[variable].fields.forEach((field) => {
      if (field.type === 'file' || field.type === 'file_multi' || field.type === 'custom') {
        // Skip unsupported field types
        return;
      }

      const id = makeVariableColumnId(action.shortId, field.id);
      if (!columnVisibility[id]) {
        return;
      }
      const customName = customNames?.[id];

      const baseField: Partial<FilterConfigField> = {
        id,
        icon: <ColumnIcon type={field.type} />,
        label: customName ? `${customName} (${field.label})` : field.label,
        type: field.type,
      };

      switch (field.type) {
        case 'multi_select':
        case 'single_select': {
          const values = getSelectFieldValues(field, lists);
          if (!values) return;
          fields.push({ ...baseField, values } as CategoryFilterFields[number]);
          break;
        }
        case 'user': {
          fields.push({
            ...baseField,
            values: (users ?? []).map((user) => ({
              label: user.name,
              value: user.shortId,
            })),
          } as CategoryFilterFields[number]);
          break;
        }
        default: {
          fields.push(baseField as CategoryFilterFields[number]);
        }
      }
    });
  });

  return fields;
};
